import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { savedTokens } from 'src/auth'
import {
  ErrorApi,
  ErrorNotLoggedIn,
  Preloader,
  SelectOption,
  Title,
} from 'src/components'
import { useReloadOrganization } from '../../Client/queries'
import { keys, useGetAdminOrganizations } from '../queries'
import OrganizationCode from './components/OrganizationCode'
import SelectWithLabel from 'src/components/Shared/UIComponents/Select'
import CustomButton from 'src/components/Shared/UIComponents/Button'

type Option = SelectOption<string>
type Options = Array<Option>

type Props = {
  username: string
}
function AdminOrganizationConfig(props: Props) {
  const queryClient = useQueryClient()
  const [organization, setOrganization] = useState<string>()
  const { data, status, error, refetch } = useGetAdminOrganizations({
    username: props.username,
  })

  const reloadOrganization = useReloadOrganization()

  const options = useMemo(
    () =>
      status === 'success'
        ? (data.map((org) => ({
            key: org.organization,
            name: org.organization,
          })) as Options)
        : [],
    [status, data],
  )

  const onReloadOrganization = useCallback(async () => {
    if (organization) {
      await reloadOrganization.mutateAsync({ organization })
      queryClient.invalidateQueries(keys.getOrg(organization))
    }
  }, [organization, reloadOrganization, queryClient])

  const onChangeOrganization = useCallback(
    (key: string) => {
      setOrganization(key)
      queryClient.invalidateQueries(keys.getOrg(key))
    },
    [queryClient],
  )

  return (
    <>
      <Title label="Organizations Config" className="mb-8" />
      {status === 'loading' || status === 'idle' ? (
        <Preloader className="col-span-full" />
      ) : status === 'error' ? (
        <div className="col-span-full">
          <ErrorApi message={error} onClick={refetch} />
        </div>
      ) : (
        <>
          <div className="flex items-center mb-8 gap-8">
            <SelectWithLabel
              placeholder="Select Organization"
              options={options}
              onChange={onChangeOrganization}
              value={organization}
            />
            <CustomButton
              isLoading={reloadOrganization.isLoading}
              disabled={!organization}
              label="Reload Organization"
              onClick={onReloadOrganization}
            />
          </div>
          {organization && (
            <OrganizationCode
              organization={organization}
              username={props.username}
            />
          )}
        </>
      )}
    </>
  )
}

export default function ValidateConfig() {
  const [tokens] = savedTokens.useState(null)
  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  return <AdminOrganizationConfig username={tokens.username} />
}
